import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface CreateEnumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  onSuccess: () => void;
}

export function CreateEnumDialog({
  open,
  onOpenChange,
  schema,
  onSuccess,
}: CreateEnumDialogProps) {
  const [name, setName] = useState("");
  const [values, setValues] = useState<string[]>([""]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addValue() {
    setValues((prev) => [...prev, ""]);
  }

  function removeValue(index: number) {
    setValues((prev) => prev.filter((_, i) => i !== index));
  }

  function updateValue(index: number, value: string) {
    setValues((prev) => prev.map((v, i) => (i === index ? value : v)));
  }

  async function handleSubmit() {
    setError(null);

    if (!name.trim()) {
      setError("Enum name is required");
      return;
    }

    const cleanValues = values.map((v) => v.trim()).filter(Boolean);
    if (cleanValues.length === 0) {
      setError("At least one value is required");
      return;
    }

    const uniqueValues = new Set(cleanValues);
    if (uniqueValues.size !== cleanValues.length) {
      setError("Duplicate values are not allowed");
      return;
    }

    setSaving(true);

    try {
      const res = await fetch("/api/database/enums", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schema, name: name.trim(), values: cleanValues }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to create enum");
        setSaving(false);
        return;
      }

      onSuccess();
      onOpenChange(false);
      toast.success(`Enum "${name}" created`);
      setName("");
      setValues([""]);
    } catch (err: any) {
      setError(err.message);
    }
    setSaving(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] sm:w-[480px]">
        <SheetHeader>
          <SheetTitle>Create Enum Type</SheetTitle>
          <SheetDescription>
            Define a custom enum type in the <span className="font-mono text-foreground">{schema}</span> schema.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="enum-name" className="text-sm">Enum Name</Label>
            <Input
              id="enum-name"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
              placeholder="status_type"
              className="h-8 text-sm font-mono"
            />
          </div>

          {/* Values */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Values</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-sm gap-1"
                onClick={addValue}
              >
                <Plus className="h-3 w-3" />
                Add Value
              </Button>
            </div>
            <div className="space-y-1.5">
              {values.map((value, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground w-5 text-right tabular-nums">
                    {i + 1}
                  </span>
                  <Input
                    aria-label={`Value ${i + 1}`}
                    value={value}
                    onChange={(e) => updateValue(i, e.target.value)}
                    placeholder={`value_${i + 1}`}
                    className="h-7 text-sm font-mono flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addValue();
                      }
                    }}
                  />
                  {values.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeValue(i)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Enum
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
